import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import matter from 'gray-matter';
import { useThemeContext } from '../context/ThemeContext';
import { useLocation } from 'react-router-dom';
import { Buffer } from 'buffer';
import { getPointerColor } from './utils/pointerUtils';
import { LinkedListRenderer } from './components/Generic/LinkedListRenderer';
import { HeapRenderer } from './components/Generic/HeapRenderer';
import { TreeRenderer } from './components/Generic/TreeRenderer';

import { renderInlineContent } from '../utils/workspaceUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

const GenericVisualizer = ({ content, metadata: passedMetadata, itemTitle, onNextChapter, nextChapterTitle, onMastered, categoryId }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';

  // Extract from metadata
  const { patternOverview } = passedMetadata || {};

  const { data: metadata, content: markdownBody } = useMemo(() => {
    try {
      const parsed = matter(content || '');
      // Prioritize passedMetadata but keep the full markdown body for fallback scraping
      return {
        data: passedMetadata || parsed.data || {},
        content: (parsed.content || '').replace(/^\ufeff/, '').trim()
      };
    } catch (err) {
      console.warn("Matter parsing failed in visualizer:", err);
      return { data: passedMetadata || {}, content: (content || '').trim() };
    }
  }, [content, passedMetadata]);

  const location = useLocation();
  const [step, setStep] = useState(0);
  const [lang, setLang] = useState(() => {
    if (location.pathname.includes('/backend/')) return 'java';
    if (location.pathname.includes('/ai/')) return 'python';
    return 'javascript';
  });

  useEffect(() => {
    setStep(0);
    // Keep it synced just in case the component stays mounted across track changes
    if (location.pathname.includes('/backend/')) {
      setLang('java');
    } else if (location.pathname.includes('/ai/')) {
      setLang('python');
    } else {
      setLang('javascript');
    }
  }, [content, location.pathname]);

  // --- Resizable Split Panel ---
  const containerRef = useRef(null);
  const [codePanelFlex, setCodePanelFlex] = useState(1.0); // flex ratio for code panel
  const isDragging = useRef(false);
  const [isDraggingState, setIsDraggingState] = useState(false);
  const startX = useRef(0);
  const startFlex = useRef(1.2);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    setIsDraggingState(true);
    startX.current = e.clientX;
    startFlex.current = codePanelFlex;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [codePanelFlex]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current || !containerRef.current) return;
      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const deltaX = e.clientX - startX.current;
      const deltaFlex = (deltaX / containerWidth) * (startFlex.current + 1);
      const newFlex = Math.min(3, Math.max(0.4, startFlex.current + deltaFlex));
      setCodePanelFlex(newFlex);
    };
    const onMouseUp = () => {
      isDragging.current = false;
      setIsDraggingState(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // --- Auto-scroll highlighted code line into view ---
  const codeContainerRef = useRef(null);
  useEffect(() => {
    if (codeContainerRef.current) {
      const activeLine = codeContainerRef.current.querySelector('[data-active-line="true"]');
      if (activeLine) {
        activeLine.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [step, lang]);

  // --- Extract Example, Use Cases, and Problem Statement from markdown body ---
  const example = useMemo(() => {
    if (metadata?.example) return metadata.example;
    if (!markdownBody) return null;
    const inputMatch = markdownBody.match(/(?:\*?\s*)?\*\*Input:\*\*\s*(.*)/i) || markdownBody.match(/Input:\s*(.*)/i);
    const outputMatch = markdownBody.match(/(?:\*?\s*)?\*\*Output:\*\*\s*(.*)/i) || markdownBody.match(/Output:\s*(.*)/i);
    const explanationMatch = markdownBody.match(/(?:\*?\s*)?\*\*Explanation:\*\*\s*([\s\S]*?)(?=\n(?:#|##|---|\*?\s*\*\*|$)|$)/i) ||
      markdownBody.match(/Explanation:\s*([\s\S]*?)(?=\n(?:#|##|---|$)|$)/i);

    if (!inputMatch && !outputMatch) return null;

    const cleanVal = (val) => val?.replace(/^[ \t·|*-]+/, '').replace(/^[`\s*]+|[`\s*]+$/g, '').trim() || '';

    return {
      input: cleanVal(inputMatch?.[1]),
      output: cleanVal(outputMatch?.[1]),
      explanation: explanationMatch?.[1]?.replace(/^[ \t·|*-]+/, '').trim() || ''
    };
  }, [markdownBody, metadata]);

  const bodyUseCases = useMemo(() => {
    if (metadata?.use_cases && metadata.use_cases.length > 0) return metadata.use_cases;
    if (!markdownBody) return [];
    // Match Use Cases section: lines following "Use Cases" heading
    const match = markdownBody.match(/use cases?[\s*]*[:\n]+((?:(?:[*-]|\d+\.)[^\n]+\n?)+)/i);
    if (!match) return [];
    const SECTION_LABELS = /^(use cases?|example|input|output|explanation|problem|note|hint|approach)s?:?$/i;
    return match[1].split('\n')
      .map((l) =>
        l.replace(/^[*-]\s*/, '')   // strip leading * or - or **
          .replace(/\*\*/g, '')     // strip bold markers
          .replace(/\*/g, '')       // strip remaining asterisks
          .trim()
      )
      .filter((l) =>
        l.length >= 6 &&          // skip very short/empty lines
        !SECTION_LABELS.test(l)   // skip section header labels caught by regex
      );
  }, [markdownBody, metadata]);

  const problemInfo = useMemo(() => {
    const {
      difficulty: metaDiff,
      pattern: metaPat,
      description: metaDesc,
      conceptExplanation: metaExp
    } = metadata || {};

    // Scrape from body as fallback if needed
    let difficulty = metaDiff || '';
    let pattern = metaPat || '';
    let description = metaDesc || '';
    let explanation = metaExp || '';

    if (!description && !explanation && markdownBody) {
      // Scrape Problem Statement for description
      const psMatch = markdownBody.match(/(?:#|##)\s*Problem Statement\s*\n+([\s\S]*?)(?=\n##\s|\n---|\n$)/i);
      if (psMatch) {
        let desc = psMatch[1];
        const diffMatch = desc.match(/Difficulty:\s*([^·\n|]+)/i);
        if (diffMatch && !difficulty) {
          difficulty = diffMatch[1].replace(/[\s·>|*_-]+$/, '').replace(/^[\s·>|*_-]+/, '').trim();
          desc = desc.replace(/Difficulty:\s*[^·\n|]+/i, '').trim();
        }
        const pattMatch = desc.match(/Pattern:\s*([^\n|]+)/i);
        if (pattMatch && !pattern) {
          pattern = pattMatch[1].replace(/[\s·>|*_-]+$/, '').replace(/^[\s·>|*_-]+/, '').trim();
          desc = desc.replace(/Pattern:\s*[^\n|]+/i, '').trim();
        }
        description = desc.replace(/[\s·>|*_-]+$/, '').replace(/^[\s·>|*_-]+/, '').trim();
      }

      // Scrape Explanation for concept intuition
      const expMatch = markdownBody.match(/(?:#|##)\s*Explanation\s*\n+([\s\S]*?)(?=\n##\s|\n---|\n$)/i);
      if (expMatch) {
        explanation = expMatch[1].replace(/\*\*/g, '').replace(/`/g, '').trim();
      }
    }

    if (difficulty || pattern || description || explanation) {
      return { difficulty, pattern, description, explanation };
    }
    return null;
  }, [markdownBody, metadata]);

  // Extract frame data using useMemo before early return
  const frameData = useMemo(() => {
    if (!metadata || !metadata.frames) return null;
    const { array, code, frames, time_complexity, space_complexity, common_use_cases } = metadata;
    const currentFrame = frames[step] || frames[0];
    const isComplete = step === frames.length - 1;
    return { array, code, frames, time_complexity, space_complexity, common_use_cases, currentFrame, isComplete };
  }, [metadata, step]);

  useEffect(() => {
    if (onMastered && frameData) onMastered(frameData.isComplete);
  }, [frameData?.isComplete, onMastered, frameData]);

  const availableLanguages = useMemo(() => {
    if (!frameData?.code) return [];
    if (typeof frameData.code === 'object' && !Array.isArray(frameData.code)) {
      return Object.keys(frameData.code);
    }
    return [];
  }, [frameData?.code]);

  const activeLang = useMemo(() => {
    if (availableLanguages.length === 0) return lang;
    return availableLanguages.includes(lang) ? lang : availableLanguages[0];
  }, [availableLanguages, lang]);

  // ── Resolve active line number (handles per-language object OR plain number) ──
  const activeLine = useMemo(() => {
    if (!frameData) return -1;
    const rawLine = frameData.currentFrame?.line;
    if (rawLine === undefined || rawLine === null) return -1;
    if (typeof rawLine === 'number') return rawLine;
    // It's a per-language object like { python: 0, javascript: 1 }
    const langLine = rawLine[activeLang] ?? rawLine[Object.keys(rawLine)[0]];
    return typeof langLine === 'number' ? langLine : -1;
  }, [frameData, activeLang]);

  // ── Build smart pointer labels from memory when pointers are number[] ──
  // e.g. memory = { left: 0, right: 3 } + pointers = [0, 3]  →  [{index:0, label:'left'}, {index:3, label:'right'}]
  const smartPointers = useMemo(() => {
    if (!frameData) return [];
    const pointers = frameData.currentFrame?.pointers || [];
    if (pointers.length === 0) return [];

    // Already in rich format (object but not array)
    if (typeof pointers[0] === 'object' && !Array.isArray(pointers[0])) {
      return pointers;
    }

    // Number[] format — try to infer labels from memory
    const numPointers = pointers;
    const memory = frameData.currentFrame?.memory || {};

    // Build a map: value → memory-key (e.g. 0 → 'left', 3 → 'right')
    const valueToLabel = {};
    for (const [key, val] of Object.entries(memory)) {
      const n = Number(val);
      if (!isNaN(n) && Number.isInteger(n) && n >= 0) {
        // Only map if the key looks like a pointer name
        const lk = key.toLowerCase();
        if (['left', 'right', 'l', 'r', 'i', 'j', 'k', 'low', 'high', 'mid', 'start', 'end', 'slow', 'fast'].includes(lk)) {
          valueToLabel[n] = key;
        }
      }
    }

    return numPointers.map((p) => {
      if (typeof p === 'number') {
        return {
          index: p,
          label: valueToLabel[p] ?? '',
        };
      }
      if (Array.isArray(p)) {
        return {
          row: p[0],
          index: p[1],
          label: p[2] || valueToLabel[p[1]] || '',
        };
      }
      if (typeof p === 'object' && p !== null) {
        return {
          ...p,
          label: p.label || valueToLabel[p.index] || '',
        };
      }
      return { index: -1, label: '' };
    }).filter((p) => p.index !== -1);
  }, [frameData, lang]);

  // --- Resolve multi-language code to lines ---
  const codeLines = useMemo(() => {
    if (!frameData?.code) return [];
    let rawCode = frameData.code;
    if (typeof rawCode === 'object' && !Array.isArray(rawCode)) {
      rawCode = rawCode[activeLang] || Object.values(rawCode)[0];
    }
    if (Array.isArray(rawCode)) return rawCode;
    if (typeof rawCode === 'string') return rawCode.split('\n');
    return [];
  }, [frameData?.code, activeLang]);

  // Detect if array contains linked list format strings
  const isLinkedListArray = useMemo(() => {
    if (!frameData?.array) return false;
    return frameData.array.some(item => typeof item === 'string' && String(item).includes('->'));
  }, [frameData?.array]);

  // Colors (these are not hooks, can stay anywhere)
  const bg = isLight ? '#ffffff' : '#0a0a0c';
  const headerBg = isLight ? '#f8fafc' : '#18181b';
  const borderColor = isLight ? '#e2e8f0' : '#27272a';
  const mutedText = isLight ? '#64748b' : '#a1a1aa';
  const textColor = isLight ? '#0f172a' : '#fff';
  const panelBg = isLight ? '#f8fafc' : '#0f0f12';
  const codeBg = isLight ? '#f1f5f9' : '#000';

  // Early return after all hooks
  if (!metadata || !metadata.frames || !frameData) return null;

  const { array, code, frames, time_complexity, space_complexity, common_use_cases, currentFrame, isComplete } = frameData;

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      margin: '0 auto',
      background: bg,
      borderRadius: '16px',
      border: `1px solid ${borderColor}`,
      overflow: 'hidden',
      fontFamily: 'sans-serif',
      color: textColor,
      boxShadow: isLight ? '0 10px 30px rgba(0,0,0,0.06)' : '0 10px 30px rgba(0,0,0,0.5)'
    }}>
      <style>{`
        @media (max-width: 600px) {
          .gv-workspace { flex-direction: column !important; height: auto !important; min-height: 0 !important; }
          .gv-code-panel { flex: none !important; height: 320px !important; border-right: none !important; border-bottom: 1px solid var(--border-main); }
          .gv-drag-handle { display: none !important; }
          .gv-visual-panel { flex: none !important; height: 340px !important; }
          .gv-mobile-hint { display: flex !important; }
        }
      `}</style>

      {/* ── MOBILE HINT BANNER ── */}
      <div className="gv-mobile-hint" style={{
        display: 'none', alignItems: 'center', justifyContent: 'space-between',
        gap: '10px', padding: '8px 14px',
        background: isLight ? 'rgba(59,130,246,0.07)' : 'rgba(59,130,246,0.1)',
        borderBottom: `1px solid rgba(59,130,246,0.2)`,
        fontSize: '12px', color: isLight ? '#1d4ed8' : '#93c5fd',
        fontWeight: 600, boxSizing: 'border-box', flexShrink: 0
      }}>
        <span>💻 Switch to a laptop for the full interactive experience.</span>
      </div>

      {/* ─── TITLE HEADER ─── */}
      {itemTitle && (
        <div style={{ padding: '20px 24px 12px', background: headerBg, borderBottom: `1px solid ${borderColor}`, display: 'flex', flexDirection: 'column', gap: '4px', boxSizing: 'border-box', maxWidth: '100%' }}>
          <span style={{ fontSize: '10px', fontWeight: 800, color: '#3b82f6', letterSpacing: '1.2px', textTransform: 'uppercase', display: 'block', opacity: 0.8 }}>PROBLEM</span>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '20px', fontWeight: 800, color: textColor, lineHeight: 1.3 }}>{itemTitle}</span>

            {/* Metadata Chips - Pushed to the right */}
            {problemInfo && (problemInfo.difficulty || problemInfo.pattern) && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {problemInfo.difficulty && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 9px',
                    borderRadius: '10px',
                    color: problemInfo.difficulty.toLowerCase() === 'easy' ? '#15803d' : problemInfo.difficulty.toLowerCase() === 'medium' ? '#d97706' : '#dc2626',
                    background: problemInfo.difficulty.toLowerCase() === 'easy' ? '#dcfce720' : problemInfo.difficulty.toLowerCase() === 'medium' ? '#fef3c720' : '#fef2f220',
                    border: `1px solid ${problemInfo.difficulty.toLowerCase() === 'easy' ? '#22c55e40' : problemInfo.difficulty.toLowerCase() === 'medium' ? '#f59e0b40' : '#ef444440'}`,
                  }}>
                    {problemInfo.difficulty}
                  </span>
                )}
                {problemInfo.pattern && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 9px',
                    lineHeight: '13px',
                    borderRadius: '10px',
                    color: isLight ? '#6366f1' : '#a5b4fc',
                    background: '#6366f115',
                    border: '1px solid #6366f130',
                  }}>
                    🧩 {problemInfo.pattern}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}



      {/* ─── ROW 2: PATTERN OVERVIEW (Full Width) ─── */}
      {metadata.patternOverview && (
        <div style={{ padding: '16px 20px', background: headerBg, borderBottom: `1px solid ${borderColor}`, boxSizing: 'border-box' }}>
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid var(--border-main)',
            position: 'relative',
            overflow: 'hidden',
            background: isLight ? 'rgba(59, 130, 246, 0.03)' : 'rgba(59, 130, 246, 0.05)',
            boxShadow: `0 4px 12px ${isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(0, 0, 0, 0.2)'}`
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, bgcolor: '#3b82f6', opacity: 0.6 }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.1rem' }}>🎯</span>
                <span style={{ fontSize: '9px', fontWeight: 800, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>Pattern Overview</span>
              </div>
            </div>
            <div className="markdown-overview" style={{
              fontSize: '15px',
              color: isLight ? '#334155' : '#cbd5e1',
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  p: ({ node, ...props }) => <p style={{ margin: '0 0 12px 0' }} {...props} />,
                  ul: ({ node, ...props }) => <ul style={{ margin: '0 0 12px 0', paddingLeft: '18px' }} {...props} />,
                  li: ({ node, ...props }) => <li style={{ marginBottom: '4px' }} {...props} />,
                  strong: ({ node, ...props }) => <strong style={{ color: isLight ? '#0f172a' : '#f8fafc', fontWeight: 800 }} {...props} />,
                  code: ({ node, inline, ...props }) => (
                    <code style={{
                      backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontFamily: 'JetBrains Mono, monospace'
                    }} {...props} />
                  )
                }}
              >
                {metadata.patternOverview}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* ─── ROW 3: PROBLEM + EXAMPLE + USE CASES (Side-by-Side) ─── */}
      {!!((problemInfo && (problemInfo.description || problemInfo.explanation) && categoryId === 'dsa') || (example && categoryId === 'dsa') || common_use_cases?.length || bodyUseCases.length) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 20px', background: headerBg, borderBottom: `1px solid ${borderColor}`, flexWrap: 'wrap', flexShrink: 0, boxSizing: 'border-box', maxWidth: '100%' }}>

          {/* Left Column: Problem Statement + Example (DSA Only) */}
          {categoryId === 'dsa' && ((problemInfo && (problemInfo.description || problemInfo.explanation)) || example) && (
            <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Problem Statement */}
              {problemInfo && (problemInfo.description || problemInfo.explanation) && (
                <div style={{
                  padding: '16px',
                  borderRadius: '12px',
                  background: isLight ? '#f9fafb' : '#0d0d10',
                  border: `1px solid ${isLight ? '#e5e7eb' : '#1f2937'}`,
                  boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px', color: '#6366f1' }}>📄</span>
                    <span style={{ fontSize: '10px', fontWeight: 900, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{problemInfo.description ? 'Problem Statement' : 'Explanation'}</span>
                  </div>
                  <div style={{
                    margin: 0,
                    fontSize: '15px',
                    color: isLight ? '#1f2937' : '#e5e7eb',
                    lineHeight: 1.5,
                    fontWeight: 500,
                    whiteSpace: 'pre-line'
                  }}>
                    {problemInfo.description
                      ? (problemInfo.description.length > 800 ? problemInfo.description.substring(0, 797) + '...' : problemInfo.description)
                      : problemInfo.explanation
                    }
                  </div>
                </div>
              )}

              {/* Example Card */}
              {example && (
                <div style={{ padding: '12px 16px', borderRadius: '8px', background: isLight ? '#fff' : '#141417', border: `1px solid ${borderColor}`, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '14px' }}>📝</span>
                    <span style={{ fontSize: '10px', fontWeight: 900, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase' }}>EXAMPLE:</span>
                  </div>
                  {example.input && <div style={{ fontSize: '14.5px', color: textColor }}><strong>Input:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13.5px', color: isLight ? '#1d4ed8' : '#93c5fd' }}>{example.input}</span></div>}
                  {example.output && <div style={{ fontSize: '14.5px', color: textColor }}><strong>Output:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13.5px', color: '#22c55e' }}>{example.output}</span></div>}
                  {example.explanation && (
                    <div className="markdown-content" style={{ fontSize: '13px', color: mutedText, marginTop: '4px', lineHeight: 1.4 }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                        components={{
                          p: ({ node, ...props }) => <span {...props} />, // Prevent extra margins from p tags
                          ul: ({ node, ...props }) => <ul style={{ margin: '4px 0', paddingLeft: '16px' }} {...props} />,
                          li: ({ node, ...props }) => <li style={{ marginBottom: '2px' }} {...props} />
                        }}
                      >
                        {example.explanation}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Right Column: Use Cases */}
          {(() => {
            const cases = (common_use_cases && common_use_cases.length > 0) ? common_use_cases : bodyUseCases;
            if (!cases || cases.length === 0) return null;
            return (
              <div style={{ flex: '0 1 235px', minWidth: '180px', padding: '12px 16px', borderRadius: '8px', background: isLight ? '#fffbeb' : '#1a1520', border: `1px solid ${isLight ? '#fcd34d40' : '#7c3aed30'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px' }}>💡</span>
                  <span style={{ fontSize: '10px', fontWeight: 900, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase' }}>Common Use Cases</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '13px', color: isLight ? '#78350f' : '#fde68a', lineHeight: 1.7 }}>
                  {cases.slice(0, 5).map((c, i) => (
                    <li key={i}>{renderInlineContent(c, '#f59e0b', isLight)}</li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── DASHBOARD / CONTROLS ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px', background: headerBg, borderBottom: `1px solid ${borderColor}`, flexShrink: 0, boxSizing: 'border-box', maxWidth: '100%' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={() => setStep(0)} style={{ padding: '5px 12px', background: isLight ? '#fff' : '#27272a', color: textColor, borderRadius: '6px', border: `1px solid ${borderColor}`, cursor: 'pointer', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            ↺ Reset
          </button>
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ padding: '5px 12px', background: isLight ? '#fff' : '#27272a', color: textColor, borderRadius: '6px', border: `1px solid ${borderColor}`, cursor: step === 0 ? 'default' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: step === 0 ? 0.5 : 1 }}>
            ← Previous
          </button>
          <button onClick={() => setStep(s => Math.min(s + 1, frames.length - 1))} disabled={isComplete} style={{ padding: '5px 18px', background: isComplete ? '#065f46' : '#2563eb', color: '#fff', borderRadius: '6px', border: 'none', cursor: isComplete ? 'default' : 'pointer', fontWeight: 600, fontSize: '13px' }}>
            {isComplete ? 'Finished ✓' : 'Next →'}
          </button>

          <span style={{ marginLeft: '12px', fontWeight: 700, fontSize: '14px', color: mutedText }}>Frame {step + 1} / {frames.length}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {time_complexity && (
            <span
              title={time_complexity}
              style={{
                padding: '4px 10px',
                background: '#3b82f620',
                color: '#60a5fa',
                border: '1px solid #3b82f6',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 700,
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
              }}
            >
              TC: {time_complexity.split(/\s|→/)[0]}
            </span>
          )}
          {space_complexity && (
            <span
              title={space_complexity}
              style={{
                padding: '4px 10px',
                background: '#10b98120',
                color: '#34d399',
                border: '1px solid #10b981',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 700,
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
              }}
            >
              SC: {space_complexity.split(/\s|→/)[0]}
            </span>
          )}
        </div>
      </div>

      {/* ─── WORKSPACE: CODE + DRAG HANDLE + VISUAL STATE ─── */}
      <div ref={containerRef} className="gv-workspace" style={{ display: 'flex', width: '100%', minHeight: '520px', height: '520px', overflow: 'hidden', boxSizing: 'border-box' }}>

        <div className="gv-code-panel" style={{ flex: codePanelFlex, display: 'flex', flexDirection: 'column', background: panelBg, borderRight: `1px solid ${borderColor}`, overflow: 'hidden', minWidth: 0 }}>

          {/* ── EXECUTION ENGINE HEADER: label + language pills ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', background: isLight ? '#f1f5f9' : '#0d0d10', borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
            <span style={{ fontSize: '10px', color: mutedText, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>EXECUTION ENGINE</span>
            {availableLanguages.length > 1 && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {availableLanguages.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    style={{
                      padding: '3px 12px',
                      background: activeLang === l ? '#2563eb' : 'transparent',
                      color: activeLang === l ? '#fff' : mutedText,
                      border: activeLang === l ? 'none' : `1px solid ${borderColor}`,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '11px',
                      borderRadius: '20px',
                      textTransform: 'lowercase',
                      letterSpacing: '0.3px',
                      transition: 'all 0.15s',
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Scrollable Code Block with line numbers */}
          <div
            ref={codeContainerRef}
            style={{
              flex: 1,
              margin: '0',
              background: codeBg,
              fontFamily: 'JetBrains Mono, Fira Code, monospace',
              fontSize: '14.2px',
              lineHeight: 1.6,
              overflowY: 'auto',
            }}
          >
            {codeLines.map((line, i) => {
              const isActiveLine = activeLine === i;
              return (
                <React.Fragment key={i}>
                  <div
                    data-active-line={isActiveLine ? 'true' : undefined}
                    style={{
                      display: 'flex',
                      background: isActiveLine ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                      borderLeft: isActiveLine ? '3px solid #3b82f6' : '3px solid transparent',
                      transition: 'background 0.2s',
                    }}
                  >
                    {/* Line number gutter */}
                    <span
                      style={{
                        display: 'inline-block',
                        minWidth: '40px',
                        padding: '0 12px 0 8px',
                        textAlign: 'right',
                        fontSize: '12px',
                        color: isActiveLine ? '#3b82f6' : (isLight ? '#cbd5e1' : '#4a4a5a'),
                        userSelect: 'none',
                        flexShrink: 0,
                        fontWeight: isActiveLine ? 700 : 400,
                      }}
                    >
                      {i + 1}
                    </span>
                    {/* Code text */}
                    <span
                      style={{
                        flex: 1,
                        padding: '0 16px 0 4px',
                        color: isActiveLine
                          ? (isLight ? '#0f172a' : '#e2e8f0')
                          : (isLight ? '#334155' : '#c9d1d9'),
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontWeight: isActiveLine ? 600 : 400,
                      }}
                    >
                      {line}
                    </span>
                  </div>
                  {/* Inline step explanation + next-line preview */}
                  {isActiveLine && (currentFrame.description || currentFrame.msg) && (
                    <motion.div
                      key={`explanation-${step}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{
                        margin: '2px 8px 2px 44px',
                        padding: '6px 12px',
                        background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.1)',
                        border: '1px solid rgba(59,130,246,0.35)',
                        borderLeft: '3px solid #3b82f6',
                        borderRadius: '0 6px 6px 0',
                        fontSize: '13px',
                        color: isLight ? '#1d4ed8' : '#93c5fd',
                        lineHeight: 1.5,
                        fontFamily: 'sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      💡 <strong>Now:</strong> {currentFrame.description || currentFrame.msg}
                    </motion.div>
                  )}
                  {/* NEXT LINE PREVIEW — shown after the active line's explanation */}
                  {isActiveLine && codeLines[i + 1] !== undefined && (
                    <div
                      style={{
                        margin: '0 8px 4px 44px',
                        padding: '3px 10px',
                        background: isLight ? '#fefce8' : 'rgba(251,191,36,0.06)',
                        border: '1px dashed rgba(251,191,36,0.4)',
                        borderLeft: '3px solid rgba(251, 191, 36, 0.7)',
                        borderRadius: '0 6px 6px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', color: isLight ? '#92400e' : '#fbbf24', opacity: 0.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{codeLines[i + 1].trim()}</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* SYSTEM MESSAGE */}
          <div style={{ padding: '0', borderTop: `1px solid ${borderColor}`, flexShrink: 0 }}>
            <div style={{ padding: '8px 16px', background: isLight ? '#fff' : '#0d0d10' }}>
              <span style={{ fontSize: '12px', color: mutedText, fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase' }}>SYSTEM MESSAGE</span>
              <div style={{ fontSize: '13px', color: isComplete ? '#10b981' : '#34d399', marginTop: '2px', lineHeight: 1.4 }}>
                {(() => {
                  const desc = currentFrame.description;
                  const msg = currentFrame.msg;
                  if (msg && msg !== desc) return msg;
                  if (isComplete) return "Execution finished successfully.";
                  return `System state updated. (Step ${step + 1} of ${frames.length})`;
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* DRAG HANDLE — VS Code style — but SLEEKER */}
        <div
          className="gv-drag-handle"
          onMouseDown={handleDragStart}
          style={{
            width: '12px',
            margin: '0 -6px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Invisible padding for better hit area */}
          <div style={{ position: 'absolute', inset: 0, width: '20px', left: '-4px' }} />

          {/* Glowing Line */}
          <motion.div
            animate={{
              width: (isDraggingState ? 4 : 2),
              backgroundColor: isDraggingState ? '#3b82f6' : 'transparent',
              opacity: isDraggingState ? 1 : 0.2,
              boxShadow: isDraggingState ? '0 0 15px rgba(59, 130, 246, 0.8)' : 'none'
            }}
            whileHover={{
              backgroundColor: '#3b82f6',
              opacity: 0.8,
              width: 3
            }}
            style={{
              height: '100%',
              borderRadius: '2px',
              background: isLight ? '#cbd5e1' : '#3f3f46',
              zIndex: 1
            }}
          />

          {/* Background Highlight when dragging */}
          <AnimatePresence>
            {isDraggingState && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent)',
                  pointerEvents: 'none',
                  zIndex: 0
                }}
              />
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT: Visual State — SCROLLABLE & EXPANDABLE */}
        <div className="gv-visual-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, background: isLight ? '#f8fafc' : '#0a0a0e' }}>

          {/* Visual State Header + Progress bar */}
          <div style={{ padding: '8px 16px 0 16px', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '10px', color: mutedText, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>VISUAL STATE</span>
              <span style={{ fontSize: '10px', color: mutedText, fontWeight: 600 }}>{step + 1} / {frames.length}</span>
            </div>
            {/* Frame progress bar */}
            <div style={{ height: '3px', background: isLight ? '#e2e8f0' : '#27272a', borderRadius: '2px', overflow: 'hidden' }}>
              <motion.div
                animate={{ width: `${((step + 1) / frames.length) * 100}%` }}
                transition={{ duration: 0.3 }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)', borderRadius: '2px' }}
              />
            </div>
          </div>

          {/* Scrollable Visual Content */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 20px 24px 20px' }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '100%', boxSizing: 'border-box' }}>

              {/* Top Row: Data Structure — full width */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '100%', boxSizing: 'border-box' }}>

                {/* ── DATA STRUCTURE VISUALIZATION ── */}
                <div style={{ width: '100%', minWidth: 0 }}>
                  <span style={{ fontSize: '10px', color: mutedText, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', letterSpacing: '1px' }}>
                    DATA STRUCTURE
                  </span>
                  <div style={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    paddingBottom: '12px',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
                  }}>
                    <style>{`
                      div::-webkit-scrollbar { height: 6px; }
                      div::-webkit-scrollbar-track { background: var(--scrollbar-track); }
                      div::-webkit-scrollbar-thumb { background-color: var(--scrollbar-thumb); border-radius: 20px; }
                    `}</style>

                    {metadata.dsType === 'linked_list' && currentFrame.memory?.list ? (
                      <LinkedListRenderer
                        listStr={currentFrame.memory.list}
                        memory={currentFrame.memory}
                        isLight={isLight}
                        textColor={textColor}
                        mutedText={mutedText}
                        cycleTo={currentFrame.cycleTo}
                      />
                    ) : metadata.dsType === 'linked_list' && array && array.every(item => typeof item === 'number' || /^\d+$/.test(String(item).trim())) ? (
                      <LinkedListRenderer
                        listStr={array.join('->')}
                        memory={currentFrame.memory || {}}
                        isLight={isLight}
                        textColor={textColor}
                        mutedText={mutedText}
                        cycleTo={currentFrame.cycleTo !== undefined ? currentFrame.cycleTo : metadata.cycleTo}
                      />
                    ) : metadata.dsType === 'tree' && metadata.tree && metadata.root ? (
                      <TreeRenderer
                        tree={metadata.tree}
                        rootId={metadata.root}
                        memory={currentFrame.memory || {}}
                        highlightIndices={[
                          ...(currentFrame.highlightIndices || []),
                          ...(currentFrame.pointers || []).map(p => typeof p === 'object' ? p.index : p)
                        ]}
                        isLight={isLight}
                        textColor={textColor}
                      />
                    ) : metadata.dsType === 'heap' ? (
                      <HeapRenderer
                        heap={(() => {
                          const h = currentFrame.memory?.heap;
                          if (typeof h === 'string') {
                            try { return JSON.parse(h); } catch (e) {
                              // Fallback for custom curriculum formats like "[(dist:8) [-2,2]]"
                              const trimmed = h.trim();
                              if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                                const inner = trimmed.slice(1, -1).trim();
                                if (!inner) return [];
                                // Split by comma, but be careful not to split nested brackets
                                // For simplicity here, we split by common pattern ", (" or ", ["
                                // Actually, most our heap strings split by ", "
                                return inner.split(/,\s+/);
                              }
                              return [];
                            }
                          }
                          return h || [];
                        })()}
                        sourceArray={metadata.array}
                        pointers={currentFrame.pointers}
                        memory={currentFrame.memory || {}}
                        isLight={isLight}
                        textColor={textColor}
                        mutedText={mutedText}
                      />
                    ) : array && isLinkedListArray ? (
                      /* Linked List Array — render each array item as a linked list chain */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '12px' }}>
                        {array.map((item, i) => {
                          const itemStr = String(item);
                          // Split "L1: 1->4->5" into label and nodes
                          const colonIdx = itemStr.indexOf(':');
                          const label = colonIdx > -1 ? itemStr.substring(0, colonIdx).trim() : `List ${i}`;
                          const chainStr = colonIdx > -1 ? itemStr.substring(colonIdx + 1).trim() : itemStr;
                          const nodes = chainStr.split('->').map(s => s.trim());

                          let isActive = false;
                          const activeLabels = [];
                          smartPointers.forEach(p => {
                            if (p.index === i) {
                              isActive = true;
                              if (p.label) activeLabels.push(p.label);
                            }
                          });
                          const primaryColor = activeLabels.length > 0 ? getPointerColor(activeLabels[0]) : null;

                          return (
                            <div key={i}>
                              {/* Pointer labels above */}
                              {activeLabels.length > 0 && (
                                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                                  {activeLabels.map(lbl => {
                                    const pc = getPointerColor(lbl);
                                    return (
                                      <motion.span
                                        key={lbl}
                                        animate={{ boxShadow: [`0 0 0px ${pc.border}40`, `0 0 8px ${pc.border}90`, `0 0 0px ${pc.border}40`] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                        style={{ fontSize: '9px', fontWeight: 900, color: pc.text, background: pc.bg, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${pc.border}80` }}
                                      >
                                        {lbl}
                                      </motion.span>
                                    );
                                  })}
                                </div>
                              )}
                              {/* List label + linked nodes */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  color: isActive ? (primaryColor?.text ?? '#3b82f6') : '#8b5cf6',
                                  background: isActive ? (primaryColor?.bg ?? '#3b82f620') : '#8b5cf620',
                                  padding: '3px 8px',
                                  borderRadius: '6px 0 0 6px',
                                  border: `1px solid ${isActive ? (primaryColor?.border ?? '#3b82f6') : '#8b5cf640'}`,
                                  borderRight: 'none',
                                  minWidth: '30px',
                                  textAlign: 'center',
                                }}>
                                  {label}
                                </span>
                                {nodes.map((node, j) => (
                                  <React.Fragment key={j}>
                                    <motion.div
                                      initial={{ scale: 0.9, opacity: 0 }}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ delay: j * 0.05 }}
                                      style={{
                                        display: 'flex',
                                        border: `2px solid ${isActive ? (primaryColor?.border ?? '#3b82f6') : (isLight ? '#3b82f6' : '#2563eb')}`,
                                        borderRadius: '6px',
                                        background: isLight ? '#fff' : '#18181b',
                                        overflow: 'hidden',
                                        boxShadow: isActive ? `0 0 10px ${primaryColor?.border ?? '#3b82f6'}40` : 'none',
                                        minWidth: '30px',
                                        height: '30px'
                                      }}
                                    >
                                      <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 8px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        borderRight: `1px solid ${isLight ? '#e2e8f0' : '#27272a'}`,
                                        color: textColor
                                      }}>
                                        {node}
                                      </div>
                                      <div style={{
                                        width: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: isLight ? '#f8fafc' : '#1e1e22'
                                      }}>
                                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: isLight ? '#64748b' : '#a1a1aa' }} />
                                      </div>
                                    </motion.div>
                                    {/* Arrow between nodes */}
                                    {j < nodes.length - 1 && (
                                      <div style={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
                                        <div style={{ width: '14px', height: '2px', background: isLight ? '#cbd5e1' : '#3f3f46', position: 'relative' }}>
                                          <div style={{
                                            position: 'absolute', right: '-2px', top: '-3px',
                                            width: '0', height: '0',
                                            borderTop: '4px solid transparent',
                                            borderBottom: '4px solid transparent',
                                            borderLeft: `5px solid ${isLight ? '#cbd5e1' : '#3f3f46'}`
                                          }} />
                                        </div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                ))}
                                {/* NULL terminator */}
                                <div style={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
                                  <div style={{ width: '14px', height: '2px', background: isLight ? '#cbd5e1' : '#3f3f46', position: 'relative' }}>
                                    <div style={{
                                      position: 'absolute', right: '-2px', top: '-3px',
                                      width: '0', height: '0',
                                      borderTop: '4px solid transparent',
                                      borderBottom: '4px solid transparent',
                                      borderLeft: `5px solid ${isLight ? '#cbd5e1' : '#3f3f46'}`
                                    }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : array && (
                      metadata.dsType === 'array' || !isLinkedListArray
                    ) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', paddingBottom: '20px' }}>
                        {((Array.isArray(array[0]) ? array : [array])).map((rowArray, rowIdx, allRows) => {
                          // Detect if first element is a row label (e.g. "s:")
                          const hasRowLabel = typeof rowArray[0] === 'string' && rowArray[0].endsWith(':');
                          const rowLabel = hasRowLabel ? rowArray[0] : null;
                          const dataArray = hasRowLabel ? rowArray.slice(1) : rowArray;

                          // Calculate row offset for global indexing (skipping labels)
                          let rowOffset = 0;
                          for (let r = 0; r < rowIdx; r++) {
                            const prevRow = allRows[r];
                            const prevHasLabel = typeof prevRow[0] === 'string' && prevRow[0].endsWith(':');
                            rowOffset += prevHasLabel ? prevRow.length - 1 : prevRow.length;
                          }

                          return (
                            <div key={rowIdx} style={{ position: 'relative' }}>
                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                                gap: '6px 0',
                                padding: '4px 0',
                                width: '100%',
                                minWidth: 0
                              }}>
                                {dataArray.map((item, i) => {
                                  const globalIdx = rowOffset + i;
                                  let isActive = false;
                                  const activeLabels = [];
                                  smartPointers.forEach((p) => {
                                    // Support { row, index } or flat index
                                    if (p.row !== undefined) {
                                      if (p.row === rowIdx && p.index === i) {
                                        isActive = true;
                                        if (p.label) activeLabels.push(p.label);
                                      }
                                    } else if (p.index === globalIdx) {
                                      isActive = true;
                                      if (p.label) activeLabels.push(p.label);
                                    }
                                  });
                                  const highlighted = currentFrame.highlightIndices?.includes(globalIdx);
                                  const primaryColor = activeLabels.length > 0 ? getPointerColor(activeLabels[0]) : null;

                                  return (
                                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: '0 0 auto' }}>

                                      {/* Pointer label + arrow above */}
                                      <AnimatePresence>
                                        {activeLabels.length > 0 ? (
                                          <motion.div
                                            key={`ptr-${rowIdx}-${i}-${activeLabels.join(',')}`}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.25 }}
                                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', marginBottom: '4px', minHeight: '36px' }}
                                          >
                                            {/* Glowing label badges */}
                                            <div style={{ display: 'flex', gap: '2px' }}>
                                              {activeLabels.map((label) => {
                                                const pc = getPointerColor(label);
                                                return (
                                                  <motion.span
                                                    key={label}
                                                    animate={{ boxShadow: [`0 0 0px ${pc.border}40`, `0 0 8px ${pc.border}90`, `0 0 0px ${pc.border}40`] }}
                                                    transition={{ duration: 1.5, repeat: Infinity }}
                                                    style={{
                                                      display: 'inline-block',
                                                      fontSize: '11px',
                                                      fontWeight: 900,
                                                      color: pc.text,
                                                      background: pc.bg,
                                                      padding: '2px 8px',
                                                      borderRadius: '4px',
                                                      border: `1px solid ${pc.border}80`,
                                                      letterSpacing: '0.3px',
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                  >
                                                    {label}
                                                  </motion.span>
                                                );
                                              })}
                                            </div>
                                            {/* Animated downward arrow */}
                                            <motion.div
                                              animate={{ y: [0, 3, 0] }}
                                              transition={{ duration: 1, repeat: Infinity }}
                                            >
                                              <svg width="14" height="9" viewBox="0 0 14 9">
                                                <polygon points="0,0 14,0 7,9" fill={primaryColor?.arrow ?? '#3b82f6'} opacity="0.9" />
                                              </svg>
                                            </motion.div>
                                          </motion.div>
                                        ) : (
                                          <div style={{ minHeight: '0', marginBottom: '4px' }} />
                                        )}
                                      </AnimatePresence>

                                      {/* Index badge - local indexing starting from 0 */}
                                      <span style={{ fontSize: '9px', color: isActive ? (primaryColor?.text ?? '#3b82f6') : mutedText, fontWeight: 700, marginBottom: '4px' }}>{i}</span>

                                      {/* Data cell + connecting line layout */}
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {/* Row label and Left bracket on first cell */}
                                        {i === 0 && (
                                          <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {rowLabel && (
                                              <span style={{
                                                fontSize: '12px',
                                                fontWeight: 800,
                                                color: isActive ? (primaryColor?.text ?? '#3b82f6') : '#8b5cf6',
                                                marginRight: '8px',
                                                minWidth: '20px',
                                                textAlign: 'right'
                                              }}>
                                                {rowLabel}
                                              </span>
                                            )}
                                            <span style={{ fontSize: '18px', color: isLight ? '#94a3b8' : '#4a5568', marginRight: '1px', lineHeight: 1, fontWeight: 300 }}>[</span>
                                          </div>
                                        )}

                                        {/* The cell itself */}
                                        <motion.div
                                          animate={{
                                            scale: isActive ? 1.12 : 1,
                                            borderColor: isActive
                                              ? (primaryColor?.border ?? '#3b82f6')
                                              : highlighted
                                                ? '#f59e0b'
                                                : (isLight ? '#d1d5db' : '#3f3f46'),
                                            background: isActive
                                              ? (primaryColor
                                                ? `linear-gradient(135deg, ${primaryColor.bg}, ${primaryColor.border}20)`
                                                : (isLight ? '#dbeafe' : '#1e3a8a'))
                                              : highlighted
                                                ? (isLight ? '#fef3c7' : '#78350f')
                                                : (isLight ? '#ffffff' : '#18181b'),
                                          }}
                                          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                                          style={{
                                            width: '38px',
                                            height: '38px',
                                            border: '2px solid',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            fontSize: '11px',
                                            color: isActive
                                              ? (primaryColor?.text ?? (isLight ? '#1e40af' : '#93c5fd'))
                                              : highlighted
                                                ? '#f59e0b'
                                                : textColor,
                                            overflow: 'hidden',
                                            cursor: 'default',
                                            position: 'relative',
                                            boxShadow: isActive
                                              ? `0 0 12px ${primaryColor?.border ?? '#3b82f6'}50`
                                              : 'none',
                                          }}
                                        >
                                          {/* Inner shine */}
                                          {isActive && (
                                            <div style={{
                                              position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
                                              background: 'linear-gradient(180deg, rgba(255,255,255,0.15), transparent)',
                                              borderRadius: '6px 6px 0 0', pointerEvents: 'none'
                                            }} />
                                          )}
                                          <span
                                            title={String(typeof item === 'object' ? String(item) : item)}
                                            style={{ display: 'block', width: '100%', textAlign: 'center', padding: '0 3px' }}
                                          >
                                            {typeof item === 'object' ? String(item) : item}
                                          </span>
                                        </motion.div>

                                        {/* Connecting dash between cells */}
                                        {i < dataArray.length - 1 && (
                                          <div style={{ width: '4px', height: '2px', background: isLight ? '#cbd5e1' : '#3f3f46', flexShrink: 0 }} />
                                        )}

                                        {/* Right bracket on last cell */}
                                        {i === dataArray.length - 1 && (
                                          <span style={{ fontSize: '18px', color: isLight ? '#94a3b8' : '#4a5568', marginLeft: '1px', lineHeight: 1, fontWeight: 300 }}>]</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* ── SYSTEM STATE (Memory Variables) ── */}
              {currentFrame.memory && (
                <div style={{ width: '100%', borderTop: `1px solid ${borderColor}`, paddingTop: '12px' }}>
                  <span style={{ fontSize: '10px', color: mutedText, fontWeight: 900, display: 'block', marginBottom: '8px', letterSpacing: '1px' }}>SYSTEM STATE</span>

                  <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '8px' }}>
                    <AnimatePresence mode="popLayout">
                      {Object.entries(currentFrame.memory || {})
                        .filter(([key]) => key !== 'list') // Don't show raw list string if handled by LL renderer
                        .map(([key, val]) => {
                          // Color-code cards by key type
                          const lk = key.toLowerCase();
                          const isPointerVar = ['left', 'right', 'l', 'r', 'i', 'j', 'k', 'low', 'high', 'mid', 'start', 'end', 'slow', 'fast', 'curr', 'prev', 'node'].includes(lk);
                          const isResultVar = ['result', 'res', 'output', 'ans', 'answer', 'found', 'match'].includes(lk);
                          const pc = isPointerVar ? getPointerColor(key) : null;
                          const cardBg = isPointerVar
                            ? (isLight ? pc?.bg : `${pc?.border}15`)
                            : isResultVar
                              ? (isLight ? '#f0fdf4' : '#14532d20')
                              : (isLight ? '#fff' : '#18181b');
                          const cardBorder = isPointerVar
                            ? `${pc?.border}60`
                            : isResultVar
                              ? '#22c55e60'
                              : (isLight ? '#e2e8f0' : '#3f3f46');
                          const valColor = isPointerVar
                            ? pc?.text
                            : isResultVar
                              ? '#22c55e'
                              : textColor;

                          return (
                            <motion.div
                              key={key}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              style={{
                                background: cardBg,
                                border: `1px solid ${cardBorder}`,
                                padding: '5px 10px',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px',
                                minWidth: '90px',
                                boxShadow: isLight ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                              }}
                            >
                              <span style={{ fontSize: '10px', fontWeight: 600, color: mutedText }}>{key}</span>
                              <span style={{ fontSize: '11px', fontWeight: 900, color: valColor, fontFamily: 'JetBrains Mono, monospace' }}>
                                {String(typeof val === 'object' ? JSON.stringify(val) : val)}
                              </span>
                            </motion.div>
                          );
                        })}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericVisualizer;